/**
 * Getypeerde toegang tot `/residences/:id/overview`.
 *
 * Eén afspraak stuurt de leesfuncties hieronder: **geld en nachten falen hard,
 * tekst faalt zacht.** Een ontbrekend bedrag stilzwijgend als € 0,00 tonen zet
 * een verkeerd getal op het scherm op precies het moment dat iemand zijn maand
 * beoordeelt; een ontbrekende gastnaam is hooguit een lege regel. Wat dragend is
 * wordt daarom gecontroleerd en leidt bij afwijking tot `invalid-response`.
 */
import { call, isRecord, readCount, readOptionalText, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type OverviewFailure = Failure
export type OverviewResult<T> = Result<T>

export interface OverviewStay {
  readonly id: string
  readonly reference: string
  readonly checkIn: string
  readonly checkOut: string
  readonly nights: number
  readonly status: string
  readonly source: string
  readonly kind: string
  readonly guests: number | null
  readonly guestName: string
  readonly totalCents: number
  readonly holdExpiresAt: string | null
}

export interface Overview {
  readonly residenceId: string
  readonly currency: string
  readonly from: string
  readonly to: string
  readonly nightsInPeriod: number
  readonly nightsBooked: number
  readonly nightsBlocked: number
  readonly occupancyPermille: number
  /** Opbrengst per beschikbare nacht, in centen. Het cijfer om op te sturen. */
  readonly revPanCents: number
  readonly revenueConfirmedCents: number
  readonly revenuePendingCents: number
  readonly receivedCents: number
  readonly bookingsConfirmed: number
  readonly bookingsPending: number
  readonly openConflicts: number
  readonly upcoming: readonly OverviewStay[]
  readonly pending: readonly OverviewStay[]
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Een bedrag in centen. Ontbreekt het of klopt het niet, dan is dat een fout. */
function readCents(source: Record<string, unknown>, field: string): number | null {
  const raw = source[field]
  return Number.isSafeInteger(raw) ? (raw as number) : null
}

function readStay(value: unknown): OverviewStay | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const checkIn = readText(value, 'checkIn')
  const checkOut = readText(value, 'checkOut')
  const totalCents = readCents(value, 'totalCents')
  if (id === '' || !CALENDAR_DATE.test(checkIn) || !CALENDAR_DATE.test(checkOut)) {
    return null
  }
  if (totalCents === null) {
    return null
  }

  const guests = value['guests']
  return {
    id,
    checkIn,
    checkOut,
    totalCents,
    reference: readText(value, 'reference'),
    nights: readCount(value, 'nights'),
    status: readText(value, 'status'),
    source: readText(value, 'source'),
    kind: readText(value, 'kind'),
    guests: Number.isSafeInteger(guests) ? (guests as number) : null,
    guestName: readText(value, 'guestName'),
    holdExpiresAt: readOptionalText(value, 'holdExpiresAt'),
  }
}

function readStayList(value: unknown): readonly OverviewStay[] {
  if (!Array.isArray(value)) {
    return []
  }
  const stays: OverviewStay[] = []
  for (const entry of value) {
    const stay = readStay(entry)
    if (stay !== null) {
      stays.push(stay)
    }
  }
  return stays
}

function readOverview(payload: unknown): Overview | null {
  if (!isRecord(payload) || !isRecord(payload['overview'])) {
    return null
  }
  const source = payload['overview']

  const revenueConfirmedCents = readCents(source, 'revenueConfirmedCents')
  const revenuePendingCents = readCents(source, 'revenuePendingCents')
  const receivedCents = readCents(source, 'receivedCents')
  const currency = readText(source, 'currency')
  if (
    revenueConfirmedCents === null ||
    revenuePendingCents === null ||
    receivedCents === null ||
    currency === ''
  ) {
    return null
  }

  return {
    currency,
    revenueConfirmedCents,
    revenuePendingCents,
    receivedCents,
    residenceId: readText(source, 'residenceId'),
    from: readText(source, 'from'),
    to: readText(source, 'to'),
    nightsInPeriod: readCount(source, 'nightsInPeriod'),
    nightsBooked: readCount(source, 'nightsBooked'),
    nightsBlocked: readCount(source, 'nightsBlocked'),
    occupancyPermille: readCount(source, 'occupancyPermille'),
    revPanCents: readCount(source, 'revPanCents'),
    bookingsConfirmed: readCount(source, 'bookingsConfirmed'),
    bookingsPending: readCount(source, 'bookingsPending'),
    openConflicts: readCount(source, 'openConflicts'),
    upcoming: readStayList(source['upcoming']),
    pending: readStayList(source['pending']),
  }
}

export async function fetchOverview(
  residenceId: string,
  range: { readonly from?: string; readonly to?: string } = {},
  options: RequestOptions = {},
): Promise<OverviewResult<Overview>> {
  const query = new URLSearchParams()
  if (range.from !== undefined) {
    query.set('from', range.from)
  }
  if (range.to !== undefined) {
    query.set('to', range.to)
  }
  const suffix = query.toString() === '' ? '' : `?${query.toString()}`

  return call(
    'GET',
    `/residences/${encodeURIComponent(residenceId)}/overview${suffix}`,
    undefined,
    readOverview,
    options,
  )
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function overviewErrorMessageKey(failure: OverviewFailure): string {
  switch (failure.code) {
    case 'not_found':
      return 'dashboard.overview.error.notFound'
    case 'forbidden':
      return 'dashboard.overview.error.forbidden'
    case 'rate_limited':
      return 'dashboard.overview.error.rateLimited'
    default:
      return 'dashboard.overview.error.unavailable'
  }
}

/** De eerste dag van de maand waarin `today` valt, als `YYYY-MM-DD`. */
export function startOfMonth(today: Date): string {
  return `${today.toISOString().slice(0, 7)}-01`
}

/**
 * De eerste dag van de maand `offset` maanden verderop.
 *
 * De periode is half-open — `to` is de eerste dag die er niet meer bij hoort —
 * dezelfde afspraak als bij `stays.check_out`. Zo zit de laatste dag van de maand
 * er precies één keer bij en niet nul of twee keer.
 */
export function monthStart(today: Date, offset: number): string {
  const moment = new Date(`${today.toISOString().slice(0, 7)}-01T00:00:00Z`)
  moment.setUTCMonth(moment.getUTCMonth() + offset)
  return moment.toISOString().slice(0, 10)
}
